/**
 * Water Ripple Visualizer - Simulates water waves with reflections
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';
import { AudioParser, WaveData } from './WaveformVisualizer';

export class WaterVisualizer extends BaseVisualizer {
  private audioParser: AudioParser;

  constructor() {
    super();
    this.audioParser = new AudioParser();
  }

  readonly metadata: VisualizerMetadata = {
    id: 'water',
    name: 'Water Ripples',
    description: 'Realistic water wave simulation with reflections',
    category: 'physics',
    defaultZoom: 0.8,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): void {
    const { ctx, width, height, activeWaves, speed, time, zoom, pan, uploadedAudioData, isUploadedAudioPlaying } = config;
    const cx = width / 2;
    const cy = height / 2;

    // When music is playing, use AudioParser for cleaner peak detection
    let displayWaves = activeWaves;
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      const parsedData = this.audioParser.parseAudioData(Array.from(uploadedAudioData));
      displayWaves = parsedData.map((w, index) => ({
        ...w,
        id: index,
        waveform: w.waveform as any,
        muted: false
      }));
    }

    if (displayWaves.length === 0) {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      return;
    }

    const imgData = ctx.createImageData(width, height);
    const data = imgData.data;

    const poolRadius = (Math.min(width, height) / 2) * 0.85 * zoom;
    const centerX = cx + pan.x;
    const centerY = cy + pan.y;

    // Pre-calculate wave properties
    const waveParams = displayWaves.map(w => {
      const effectiveFreq = w.freq * speed;
      return {
        amp: w.amp,
        k: effectiveFreq * 0.5,
        speedFactor: effectiveFreq * 8,
        phaseOffset: (w.phase * Math.PI) / 180
      };
    });

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const dx = x - centerX;
        const dy = y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const idx = (y * width + x) * 4;

        if (dist > poolRadius) {
          // Outside pool - dark background
          data[idx] = 17; 
          data[idx + 1] = 24; 
          data[idx + 2] = 39; 
          data[idx + 3] = 255;
          continue;
        }

        const rNorm = dist / poolRadius;
        let h = 0;
        let gradX = 0;
        let gradY = 0;

        for (const w of waveParams) {
          const rVal = rNorm * 20;
          
          // Outward wave
          const argOut = (w.k * rVal) - (time * w.speedFactor) + w.phaseOffset;
          const wOut = Math.sin(argOut);

          // Reflected inward wave
          const rRefl = (2 - rNorm) * 20;
          const argIn = (w.k * rRefl) - (time * w.speedFactor) + w.phaseOffset;
          const wIn = Math.sin(argIn);

          const damping = 0.7;
          const waveH = w.amp * (wOut - (wIn * damping));
          h += waveH;

          // Gradient for lighting
          const dOut = w.k * Math.cos(argOut);
          const dIn = w.k * Math.cos(argIn) * -1 * damping;
          const dHdr = w.amp * (dOut - dIn);

          const safeDist = dist < 1 ? 1 : dist;
          gradX += dHdr * (dx / safeDist);
          gradY += dHdr * (dy / safeDist);
        }

        // Lighting model
        const slopeScale = 0.02;
        const sx = gradX * slopeScale;
        const sy = gradY * slopeScale;

        const lx = -0.6;
        const ly = -0.6;
        const diffuse = (sx * lx + sy * ly);
        const heightShade = h * 1.2;
        const specular = Math.pow(Math.max(0, diffuse - 0.2), 16) * 60;
        const lightIntensity = (diffuse * 60);

        data[idx] = Math.max(0, Math.min(255, 0 + specular + heightShade * 0.5));
        data[idx + 1] = Math.max(0, Math.min(255, 50 + lightIntensity + specular + heightShade));
        data[idx + 2] = Math.max(0, Math.min(255, 110 + lightIntensity + specular + heightShade));
        data[idx + 3] = 255;
      }
    }

    ctx.putImageData(imgData, 0, 0);

    // Draw pool border
    ctx.beginPath();
    ctx.arc(centerX, centerY, poolRadius, 0, Math.PI * 2);
    ctx.strokeStyle = '#60a5fa';
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}
